import type { WhatsappIntegration, WhatsappProviderSummary } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { ApiError } from '../../../../../shared/shared.api';
import { apiErrorReason } from '../../../policies/api-error-reason.policy';
import { listWhatsappIntegrations, listWhatsappProviders, startWhatsappSending, stopWhatsappSending, uninstallWhatsappIntegration } from '../whatsapp.api';

export type WhatsappIntegrationsState =
  | { status: 'loading' }
  | { status: 'loaded'; providers: WhatsappProviderSummary[]; integrations: WhatsappIntegration[] }
  | { status: 'forbidden' }
  | { status: 'error' };

/** The WhatsApp providers this release ships, the ones installed, and switching between them. Row actions have no form, so a failure toasts. */
export function useWhatsappIntegrations() {
  const toast = useToast();
  const [state, setState] = useState<WhatsappIntegrationsState>({ status: 'loading' });
  // Bumped after a change, to read the list again; the old list stays on screen meanwhile.
  const [version, setVersion] = useState(0);
  const reload = () => setVersion((current) => current + 1);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listWhatsappProviders(), listWhatsappIntegrations()])
      .then(([providers, integrations]) => !cancelled && setState({ status: 'loaded', providers, integrations }))
      .catch((err) => !cancelled && setState({ status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' }));
    return () => {
      cancelled = true;
    };
  }, [version]);

  async function act(action: () => Promise<unknown>, success: string, failure: string): Promise<void> {
    try {
      await action();
      reload();
      toast.success(success);
    } catch (err) {
      toast.error(apiErrorReason(err, failure));
    }
  }

  return {
    state,
    reload,
    startSending: (item: WhatsappIntegration) =>
      act(() => startWhatsappSending(item.id), `${item.displayName} now sends your WhatsApp messages`, `Couldn't switch to ${item.displayName}`),
    stopSending: (item: WhatsappIntegration) =>
      act(() => stopWhatsappSending(item.id), `${item.displayName} turned off`, `Couldn't turn off ${item.displayName}`),
    uninstall: (item: WhatsappIntegration) =>
      act(() => uninstallWhatsappIntegration(item.id), `${item.displayName} uninstalled`, `Couldn't uninstall ${item.displayName}`),
  };
}
